import type { ReactNode } from "react";

import CardTypeChips from "../components/CardTypeChips/CardTypeChips";
import { CRMIntegrationCard } from "../components/integration-cards/CRMIntegrationCard/CRMIntegrationCard";
import { FindNewCompaniesCard } from "../components/integration-cards/FindNewCompaniesCard/FindNewCompaniesCard";
import { SignalsRestApiCard } from "../components/integration-cards/SignalsRestApiCard/SignalsRestApiCard";
import { SignalsWebhookCard } from "../components/integration-cards/SignalsWebhookCard/SignalsWebhookCard";
import type { CardType } from "../constants/cardTypes";
import { CARD_TYPE } from "../constants/cardTypes";
import { useCRMIntegrationStatusQuery } from "../query/queries/useCRMIntegrationStatusQuery";
import isCRMIntegrated from "../utils/isCRMIntegrated";

import useFindNewCompaniesData from "./useFindNewCompaniesData";
import useSignalsApiData from "./useSignalsApiData";
import { ExportIntegrations } from "app/pages/sales-intelligence/pages/management/types/types";
import useSignalsRestApiData from "./useSignalsRestApiData";

interface IIntegrationCardConfig {
    id: string;
    types: readonly CardType[];
    node: ReactNode;
}

interface IUseMyIntegrationCardsResult {
    cards: IIntegrationCardConfig[];
    isLoading: boolean;
}

export const useMyIntegrationCards = (): IUseMyIntegrationCardsResult => {
    const salesforceStatusQuery = useCRMIntegrationStatusQuery(ExportIntegrations.salesforce);
    const hubspotStatusQuery = useCRMIntegrationStatusQuery(ExportIntegrations.hubspot);
    const dynamicsStatusQuery = useCRMIntegrationStatusQuery(ExportIntegrations.dynamics);

    const signalsApiData = useSignalsApiData();
    const signalsRestApiData = useSignalsRestApiData();
    const findNewCompaniesProps = useFindNewCompaniesData();

    const salesforceData = salesforceStatusQuery.data;
    const hubspotData = hubspotStatusQuery.data;
    const dynamicsData = dynamicsStatusQuery.data;

    const isLoading =
        salesforceStatusQuery.isLoading ||
        hubspotStatusQuery.isLoading ||
        dynamicsStatusQuery.isLoading ||
        (signalsApiData.isFetching && signalsApiData.isStale) ||
        (signalsRestApiData.isFetching && signalsRestApiData.isStale) ||
        (findNewCompaniesProps.isFetching && findNewCompaniesProps.isStale);

    const salesforceItem = isCRMIntegrated(salesforceData)
        ? {
              id: "salesforce",
              types: [CARD_TYPE.CRM],
              node: (
                  <CRMIntegrationCard
                      integrationName={ExportIntegrations.salesforce}
                      crmData={salesforceData}
                      onRefetch={salesforceStatusQuery.silentRefetch}
                  >
                      <CardTypeChips types={[CARD_TYPE.CRM]} />
                  </CRMIntegrationCard>
              ),
          }
        : null;

    const hubspotItem = isCRMIntegrated(hubspotData)
        ? {
              id: "hubspot",
              types: [CARD_TYPE.CRM],
              node: (
                  <CRMIntegrationCard
                      integrationName={ExportIntegrations.hubspot}
                      crmData={hubspotData}
                      onRefetch={hubspotStatusQuery.silentRefetch}
                  >
                      <CardTypeChips types={[CARD_TYPE.CRM]} />
                  </CRMIntegrationCard>
              ),
          }
        : null;

    const signalsRestApiItem = signalsRestApiData.isConfigured
        ? {
              id: "signalsRestApi",
              types: [CARD_TYPE.API],
              node: (
                  <SignalsRestApiCard {...signalsRestApiData}>
                      <CardTypeChips types={[CARD_TYPE.API]} />
                  </SignalsRestApiCard>
              ),
          }
        : null;

    const dynamicsItem = isCRMIntegrated(dynamicsData)
        ? {
              id: "dynamics",
              types: [CARD_TYPE.CRM],
              node: (
                  <CRMIntegrationCard
                      integrationName={ExportIntegrations.dynamics}
                      crmData={dynamicsData}
                      onRefetch={dynamicsStatusQuery.silentRefetch}
                  >
                      <CardTypeChips types={[CARD_TYPE.CRM]} />
                  </CRMIntegrationCard>
              ),
          }
        : null;

    const signalsApiItem =
        signalsApiData.data && signalsApiData.isConfigured
            ? {
                  id: "signalsApi",
                  types: [CARD_TYPE.Webhook],
                  node: (
                      <SignalsWebhookCard {...signalsApiData}>
                          <CardTypeChips types={[CARD_TYPE.Webhook]} />
                      </SignalsWebhookCard>
                  ),
              }
            : null;

    const findNewCompaniesItem =
        findNewCompaniesProps.webhooks && findNewCompaniesProps.isConfigured
            ? {
                  id: "findNewCompanies",
                  types: [CARD_TYPE.Webhook],
                  node: (
                      <FindNewCompaniesCard {...findNewCompaniesProps}>
                          <CardTypeChips types={[CARD_TYPE.Webhook]} />
                      </FindNewCompaniesCard>
                  ),
              }
            : null;

    const cards: IIntegrationCardConfig[] = [
        salesforceItem,
        hubspotItem,
        dynamicsItem,
        signalsRestApiItem,
        signalsApiItem,
        findNewCompaniesItem,
    ].filter((item) => !!item);

    return { cards, isLoading };
};
